import React, { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Upload, X, Download, FileArchive, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useEmbroideryConversion, EmbroiderySettings } from '@/hooks/useEmbroideryConversion';
import { toast } from 'sonner';

const EMBROIDERY_FORMATS = [
  { value: 'DST', label: 'DST (Tajima)' },
  { value: 'PES', label: 'PES (Brother/Babylock)' },
  { value: 'JEF', label: 'JEF (Janome)' },
  { value: 'EXP', label: 'EXP (Melco/Bernina)' },
  { value: 'VP3', label: 'VP3 (Pfaff/Husqvarna)' },
  { value: 'XXX', label: 'XXX (Singer)' },
  { value: 'HUS', label: 'HUS (Husqvarna Viking)' },
  { value: 'VIP', label: 'VIP (Pfaff)' },
  { value: 'SEW', label: 'SEW (Janome)' },
  { value: 'CSD', label: 'CSD (Poem)' },
];

const THREAD_BRANDS = [
  'Madeira',
  'Robison-Anton',
  'Isacord',
  'Sulky',
  'Brother',
  'Janome'
];

export const EmbroideryConverter: React.FC = () => {
  const {
    images,
    isProcessing,
    addImages,
    removeImage,
    convertImages,
    downloadConversion,
    downloadAllFormats,
    downloadAll,
    clearAll
  } = useEmbroideryConversion();

  const [settings, setSettings] = useState<EmbroiderySettings>({
    outputFormats: ['DST', 'PES'],
    stitchDensity: 'medium',
    maxStitchCount: 50000,
    colorCount: 8,
    designDimensions: {
      width: 100,
      height: 100,
      unit: 'mm'
    },
    threadBrand: 'Madeira',
    stitchAngle: 45,
    addUnderlay: true,
    pullCompensation: 10
  });

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 20) {
      toast.error('Maximum 20 images allowed per batch');
      return;
    }
    addImages(files);
  }, [addImages]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type.startsWith('image/')
    );
    if (files.length > 20) {
      toast.error('Maximum 20 images allowed per batch');
      return;
    }
    addImages(files);
  }, [addImages]);

  const toggleFormat = (format: string) => {
    setSettings(prev => ({
      ...prev,
      outputFormats: prev.outputFormats.includes(format)
        ? prev.outputFormats.filter(f => f !== format)
        : [...prev.outputFormats, format]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card className="p-6">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="embroidery-upload"
          />
          <label htmlFor="embroidery-upload" className="cursor-pointer">
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-semibold mb-2">Upload Images for Embroidery Conversion</p>
            <p className="text-sm text-muted-foreground">
              Drag and drop or click to upload (max 20 images)
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Supports: PNG, JPG, SVG
            </p>
          </label>
        </div>
      </Card>

      {/* Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Conversion Settings</h3>
        
        <div className="space-y-6">
          {/* Output Formats */}
          <div>
            <Label className="text-base mb-3 block">Output Formats</Label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {EMBROIDERY_FORMATS.map(format => (
                <div key={format.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={format.value}
                    checked={settings.outputFormats.includes(format.value)}
                    onCheckedChange={() => toggleFormat(format.value)}
                  />
                  <Label htmlFor={format.value} className="cursor-pointer">
                    {format.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Stitch Density */}
          <div>
            <Label className="text-base mb-3 block">Stitch Density</Label>
            <RadioGroup
              value={settings.stitchDensity}
              onValueChange={(value: any) => setSettings(prev => ({ ...prev, stitchDensity: value }))}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="low" id="low" />
                <Label htmlFor="low">Low</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="medium" id="medium" />
                <Label htmlFor="medium">Medium</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="high" id="high" />
                <Label htmlFor="high">High</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Design Dimensions */}
            <div>
              <Label className="text-base mb-3 block">Design Size</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Width"
                  value={settings.designDimensions.width}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    designDimensions: {
                      ...prev.designDimensions,
                      width: parseInt(e.target.value) || 100
                    }
                  }))}
                />
                <Input
                  type="number"
                  placeholder="Height"
                  value={settings.designDimensions.height}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    designDimensions: {
                      ...prev.designDimensions,
                      height: parseInt(e.target.value) || 100
                    }
                  }))}
                />
                <Select
                  value={settings.designDimensions.unit}
                  onValueChange={(value: any) => setSettings(prev => ({
                    ...prev,
                    designDimensions: { ...prev.designDimensions, unit: value }
                  }))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mm">mm</SelectItem>
                    <SelectItem value="inch">inch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Color Count */}
            <div>
              <Label className="text-base mb-3 block">Color Count (2-15)</Label>
              <Input
                type="number"
                min={2}
                max={15}
                value={settings.colorCount}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  colorCount: Math.min(15, Math.max(2, parseInt(e.target.value) || 8))
                }))}
              />
            </div>

            {/* Max Stitch Count */}
            <div>
              <Label className="text-base mb-3 block">Max Stitch Count</Label>
              <Input
                type="number"
                value={settings.maxStitchCount}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  maxStitchCount: parseInt(e.target.value) || 50000
                }))}
              />
            </div>

            {/* Thread Brand */}
            <div>
              <Label className="text-base mb-3 block">Thread Brand</Label>
              <Select
                value={settings.threadBrand}
                onValueChange={(value) => setSettings(prev => ({ ...prev, threadBrand: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THREAD_BRANDS.map(brand => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-3">Advanced Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stitch Angle: {settings.stitchAngle}°</Label>
                <input
                  type="range"
                  min={0}
                  max={180}
                  step={15}
                  value={settings.stitchAngle}
                  onChange={(e) => setSettings(prev => ({ ...prev, stitchAngle: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label>Pull Compensation: {settings.pullCompensation}%</Label>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={settings.pullCompensation}
                  onChange={(e) => setSettings(prev => ({ ...prev, pullCompensation: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="addUnderlay"
                  checked={settings.addUnderlay}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, addUnderlay: !!checked }))}
                />
                <Label htmlFor="addUnderlay" className="cursor-pointer">
                  Add Underlay Stitches
                </Label>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      {images.length > 0 && (
        <div className="flex gap-3">
          <Button
            onClick={() => convertImages(settings)}
            disabled={isProcessing}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Converting...
              </>
            ) : (
              `Convert ${images.length} Image(s)`
            )}
          </Button>
          <Button
            onClick={downloadAll}
            variant="outline"
            disabled={!images.some(img => img.status === 'completed')}
          >
            <FileArchive className="w-4 h-4 mr-2" />
            Download All
          </Button>
          <Button onClick={clearAll} variant="outline">
            Clear All
          </Button>
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {images.map(image => (
            <Card key={image.id} className="p-4">
              <div className="flex items-start gap-4">
                <img
                  src={image.preview}
                  alt={image.file.name}
                  className="w-24 h-24 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-medium truncate">{image.file.name}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {image.status === 'processing' && (
                    <Progress value={image.progress} className="mb-2" />
                  )}

                  {image.status === 'completed' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle2 className="w-4 h-4" />
                        Converted to {image.conversions.length} format(s)
                      </div>
                      
                      {/* Preview SVG - sanitized with DOMPurify for XSS protection */}
                      {image.previewSvg && (
                        <div className="border rounded-lg p-2 bg-white">
                          <div 
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(image.previewSvg, { 
                                USE_PROFILES: { svg: true, svgFilters: true },
                                ADD_TAGS: ['pattern'],
                                ADD_ATTR: ['patternUnits']
                              }) 
                            }}
                            className="max-h-32 overflow-hidden"
                          />
                        </div>
                      )}

                      {/* Stats */}
                      {image.conversions[0] && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>Stitches: {image.conversions[0].stitchCount?.toLocaleString()}</div>
                          <div>Colors: {image.conversions[0].colors?.length}</div>
                          <div>Formats: {image.conversions.length}</div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1">
                        {image.conversions.map(conv => (
                          <Button
                            key={conv.format}
                            size="sm"
                            variant="outline"
                            onClick={() => downloadConversion(image, conv.format)}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            {conv.format}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadAllFormats(image)}
                        >
                          <FileArchive className="w-3 h-3 mr-1" />
                          All
                        </Button>
                      </div>
                    </div>
                  )}

                  {image.status === 'failed' && (
                    <div className="flex items-center gap-2 text-sm text-destructive">
                      <XCircle className="w-4 h-4" />
                      {image.errorMessage || 'Conversion failed'}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
